import { useMemo, useState } from "react";
import { FieldArray, useFormikContext } from "formik";
import FieldArrayContainer from "../fieldArrayContainer";
import FieldArrayFields from "../fieldArrayContainer/FieldArrayFields";
import FormInput from "../input/FormInput";
import {
  getRLFormFields,
  getRlTableColumns,
} from "../../../utils/helpers/relatedListHelpers";
import { IFieldInstance } from "../../../types/fieldInstance.interface";
import { getInitialValues } from "../../../utils/formGenUtils/getInitialValues";
import { isActiveRecordSubmittable } from "../../../utils/formGenUtils/relatedListUtils/isActiveRecordSubmittable";

export interface IRlFormComp {
  fieldName: string;
  fieldLabel: string;
  getError: any;
  value: any;
  selectOptions: any;
  isRequired: boolean;
  disabled?: boolean;
  rlViewModel: any;
  setDeleteRLRecordObject: (value: any) => void;
  showBadges: boolean;
  qapi: boolean;
}

const RLFormComp = ({
  fieldName,
  fieldLabel,
  getError,
  value,
  selectOptions,
  isRequired,
  disabled,
  rlViewModel,
  setDeleteRLRecordObject,
  showBadges,
  qapi,
}: IRlFormComp) => {
  const fields = getRLFormFields(fieldName);
  const _fields = Object.values(fields).flat();
  const { allLookUpOptions } = selectOptions;
  const [openModal, setOpenModal] = useState(false);
  const initialValues = getInitialValues(_fields as IFieldInstance[]);
  const fieldErrors = getError(fieldName);
  const errorMessage = typeof fieldErrors === "string" ? fieldErrors : "";
  const { setFieldTouched } = useFormikContext();

  const data = useMemo(() => value || [], [value]);
  const [active, setActive] = useState();
  const [editMode, setEditMode] = useState(false);
  const [valueToEdit, setValueToEdit] = useState();

  function isHidden(index: number) {
    if (typeof active === "number") {
      return value.length > 0 && index !== active ? "hidden" : "";
    }
    return value.length > 1 && index !== value.length - 1 ? "hidden" : "";
  }

  function _isActiveRecordSubmittable() {
    return isActiveRecordSubmittable(
      active,
      value,
      fieldName,
      rlViewModel,
      _fields as IFieldInstance[]
    );
  }

  function handleClick(array: any) {
    array.push(initialValues);
  }

  const columns = useMemo(
    () => getRlTableColumns(_fields as IFieldInstance[], allLookUpOptions),
    [fieldName]
  );

  return (
    <div>
      <FormInput
        name={fieldName}
        label={fieldLabel}
        isImportant={isRequired}
        errorMessage={errorMessage}
        showBadges={showBadges}
        qapi={qapi}
      >
        <FieldArray
          name={fieldName}
          render={(arrayHelpers) => (
            <>
              <FieldArrayContainer
                data-testid={`related-list-container-${fieldName}`}
                openModal={openModal}
                setOpenModal={setOpenModal}
                handleClick={handleClick}
                arrayHelpers={arrayHelpers}
                setFieldTouched={setFieldTouched}
                setDeleteRLRecordObject={setDeleteRLRecordObject}
                validateForm={_isActiveRecordSubmittable}
                active={active}
                setActive={setActive}
                editMode={editMode}
                setEditMode={setEditMode}
                valueToEdit={valueToEdit}
                setValueToEdit={setValueToEdit}
                columns={columns}
                data={data}
                disabled={disabled}
              >
                {(value || []).map((record: any, index: any) => {
                  return (
                    <FieldArrayFields
                      key={index}
                      index={index}
                      mainFieldName={fieldName}
                      record={record}
                      fields={fields}
                      viewModel={rlViewModel}
                      getError={getError}
                      selectOptions={selectOptions}
                      isHidden={isHidden}
                    />
                  );
                })}
              </FieldArrayContainer>
            </>
          )}
        />
      </FormInput>
    </div>
  );
};

export default RLFormComp;
